/* Gemeinsamer Speicher der Transferhistorie je Spieler (Modul ohne Datenbankzugriff).
 *
 * Die Datenbankfunktionen werden hereingereicht (api), damit sich Laden, Teilen der
 * Abfrage und das sofortige Nachziehen nach Anlegen/Löschen ohne Supabase prüfen lassen
 * (transferStore.test.js). Benutzt wird er in composables/useTransfers.js.
 */

import { reactive } from 'vue';
import { sortTransfers } from '../domain/transfers.js';

/**
 * Gemeinsamer Speicher der Listen je Spieler.
 * api: { list(playerId), insert(playerId, t), update(id, playerId, t), remove(id) }
 */
export function createTransferStore(api) {
  const byPlayer = reactive({}); // playerId -> sortierte Liste; fehlt der Schlüssel: noch nicht geladen
  const loadErrors = reactive({}); // playerId -> Fehlerschlüssel der letzten Abfrage
  const loadingIds = reactive({}); // playerId -> true während einer Abfrage
  const inflight = new Map();

  /**
   * Liste laden. Ohne `force` wird eine schon geladene Liste nicht erneut geholt;
   * eine laufende Abfrage wird in beiden Fällen mitbenutzt (keine doppelte Anfrage).
   */
  function load(id, { force = false } = {}) {
    if (!id) return Promise.resolve();
    if (inflight.has(id)) return inflight.get(id);
    if (!force && id in byPlayer) return Promise.resolve();
    loadingIds[id] = true;
    const job = (async () => {
      try {
        byPlayer[id] = sortTransfers(await api.list(id));
        loadErrors[id] = null;
      } catch (e) {
        /* Beim Fehler bleibt eine vorher geladene Liste stehen; war keine da, bleibt der
           Spieler "nicht geladen" und der nächste Versuch fragt erneut. */
        loadErrors[id] = e.message || 'loadFailed';
      } finally {
        loadingIds[id] = false;
        inflight.delete(id);
      }
    })();
    inflight.set(id, job);
    return job;
  }

  async function add(id, transfer) {
    const saved = await api.insert(id, transfer);
    byPlayer[id] = sortTransfers([...(byPlayer[id] || []), saved]);
    return saved;
  }

  async function update(id, transferId, transfer) {
    const saved = await api.update(transferId, id, transfer);
    byPlayer[id] = sortTransfers((byPlayer[id] || []).map((t) => (t.id === transferId ? saved : t)));
    return saved;
  }

  async function remove(id, transferId) {
    await api.remove(transferId);
    byPlayer[id] = (byPlayer[id] || []).filter((t) => t.id !== transferId);
  }

  const itemsOf = (id) => (id ? byPlayer[id] || [] : []);
  const isLoading = (id) => !!(id && loadingIds[id]);
  const errorOf = (id) => (id ? loadErrors[id] || null : null);

  return { load, add, update, remove, itemsOf, isLoading, errorOf };
}
